import { Text, VStack, Box, chakra } from '@chakra-ui/react';

import type { FormFieldAnswer, FormSection } from '@duke-hq/shared-constants/assessments';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';
import { hasTiptapContent } from '@duke-hq/lib/dukeTiptapEditorHelpers';

import { TextField } from './fields/TextField';
import { RadioGroupField } from './fields/RadioGroupField';
import { DropdownSelectField } from './fields/DropdownSelectField';
import { getFieldPlaceholder } from './utils';

// Rolling our own grid so we can use 'subgrid' for field rendering
// This allows inputs on a single line to be aligned regardless of label/ helper or errors.
const FormGrid = chakra('div', {
  baseStyle: {
    display: 'grid',
    gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
    gridTemplateRows: 'auto minmax(0, auto) 1fr minmax(0, auto)',
    columnGap: 4,
    rowGap: 0,
    width: '100%',
    '&[data-view-mode="mobile"]': {
      gridTemplateColumns: '1fr',
    },
    '.assessment-form-field': {
      display: 'grid',
      gridTemplateRows: 'subgrid',
      gridRow: 'span 4',
      marginBottom: '1rem',
    },
    '.assessment-form-field-full': {
      gridColumn: '1 / -1',
    },
  },
});

type AssessmentFormRendererProps = {
  slide: {
    id: string;
    sections: FormSection[];
  };
  formSlideState?: Record<string, { textInputValue?: string; selectedOptions?: string[] }>;
  formFieldErrors?: Record<string, string>;
  isReadOnly?: boolean;
  isReviewResults?: boolean;
  answerMap?: Map<string, FormFieldAnswer>;
  onTextBlur?: (params: { fieldId: string; value: string; required: boolean }) => Promise<void>;
  onTextChange?: (params: { fieldId: string; value: string; required: boolean }) => void;
  onOptionChange?: (params: { fieldId: string; value: string; required: boolean }) => Promise<void>;
  viewMode?: 'mobile' | 'desktop';
  revisionNumber?: number;
};

export function AssessmentFormRenderer({
  slide,
  formSlideState = {},
  formFieldErrors = {},
  isReadOnly = false,
  isReviewResults = false,
  answerMap = new Map(),
  onTextBlur = async () => {},
  onTextChange,
  onOptionChange = async () => {},
  viewMode,
}: AssessmentFormRendererProps) {
  return (
    <VStack spacing="0" alignItems="flex-start" mt="16px">
      {slide.sections.map((section) => (
        <VStack key={section.id} alignItems="flex-start" w="full">
          {section.title.length > 0 ? (
            <Text fontWeight="semibold" fontSize="xl" mt={2}>
              {section.title}
            </Text>
          ) : null}

          {hasTiptapContent(section.description) ? (
            <EditorHTML content={section.description} style={{ padding: 0, margin: 0 }} />
          ) : null}

          <FormGrid data-view-mode={viewMode}>
            {section.fields?.map((field) => {
              const isFieldEditable = field.id in formSlideState;
              const fieldAnswer = answerMap.get(field.id);
              const fieldValue = isFieldEditable ? formSlideState[field.id] : fieldAnswer;

              // Show correct indicator when:
              // 1. Review results mode (when showing all correct/incorrect)
              // 2. Fields that are readonly (not in formSlideState) and marked correct
              const showCorrectIndicator =
                isReviewResults || (!isFieldEditable && fieldAnswer?.correct === true);

              const commonProps = {
                id: field.id,
                label: field.label,
                helperText: field.helperText ?? '',
                error: formFieldErrors[field.id],
                isRequired: field.required,
                isReadOnly: isReadOnly || !isFieldEditable,
                isCorrect: showCorrectIndicator ? fieldAnswer?.correct : undefined,
              };

              const isFullWidth = Boolean(field.fullWidthLayout) && viewMode !== 'mobile';

              return (
                <Box
                  key={field.id}
                  className={`assessment-form-field ${isFullWidth ? 'assessment-form-field-full' : ''}`}
                >
                  {field.fieldType === 'TEXT' ? (
                    <TextField
                      key={field.id}
                      placeholder={getFieldPlaceholder('TEXT', field.label, field.placeholder)}
                      isMultiline={field.multiline ?? false}
                      onChange={
                        onTextChange
                          ? (value: string) =>
                              onTextChange({
                                fieldId: field.id,
                                value,
                                required: field.required ?? false,
                              })
                          : undefined
                      }
                      onBlur={async (value: string) =>
                        onTextBlur({
                          fieldId: field.id,
                          value,
                          required: field.required ?? false,
                        })
                      }
                      value={fieldValue?.textInputValue ?? ''}
                      {...commonProps}
                    />
                  ) : field.fieldType === 'RADIO' ? (
                    <RadioGroupField
                      key={field.id}
                      options={field.options ?? []}
                      onChange={async (value: string) =>
                        onOptionChange({
                          fieldId: field.id,
                          value,
                          required: field.required ?? false,
                        })
                      }
                      value={fieldValue?.selectedOptions?.[0] ?? ''}
                      {...commonProps}
                    />
                  ) : field.fieldType === 'SELECT' ? (
                    <DropdownSelectField
                      key={field.id}
                      placeholder={getFieldPlaceholder('SELECT', field.label, field.placeholder)}
                      options={field.options ?? []}
                      onChange={async (value: string) =>
                        onOptionChange({
                          fieldId: field.id,
                          value,
                          required: field.required ?? false,
                        })
                      }
                      value={fieldValue?.selectedOptions?.[0] ?? ''}
                      {...commonProps}
                    />
                  ) : null}
                </Box>
              );
            })}
          </FormGrid>
        </VStack>
      ))}
    </VStack>
  );
}
